import { GeoJson, LineCoordinates } from './types';

const spatialReference = { wkid: 4326 };

const convertPoint = (geoJson: GeoJson) => ({
  x: geoJson.coordinates[0],
  y: geoJson.coordinates[1],
  spatialReference,
});

const convertLineString = (geoJson: GeoJson) => ({
  paths: [geoJson?.coordinates],
  spatialReference,
});

const convertPolygon = (geoJson: GeoJson) => ({
  rings: geoJson?.coordinates,
  spatialReference,
});

const convertMultiPoint = (geoJson: GeoJson) => ({
  points: geoJson?.coordinates,
  spatialReference,
});

const convertMultiLineString = (geoJson: GeoJson) => ({
  paths: geoJson?.coordinates,
  spatialReference,
});

const convertMultiPolygon = (geoJson: GeoJson) => ({
  rings: geoJson?.coordinates?.flat(),
  spatialReference,
});

const convertEnvelope = (geoJson: GeoJson) => {
  const coordinates = geoJson.coordinates[0] as LineCoordinates;

  const [xmin, ymin] = coordinates[0];
  const [xmax, ymax] = coordinates[2];
  return {
    xmin,
    ymin,
    xmax,
    ymax,
    spatialReference,
  };
};

const convertFnMap = {
  Envelope: convertEnvelope, // For polygons with bbox
  LineString: convertLineString,
  MultiLineString: convertMultiLineString,
  MultiPoint: convertMultiPoint,
  MultiPolygon: convertMultiPolygon,
  Point: convertPoint,
  Polygon: convertPolygon,
};

export const convertGeoJsonToEsri = (geoJson: GeoJson) => {
  const convertFn = convertFnMap[geoJson.type];

  if (!geoJson || !geoJson.type || !convertFn) {
    throw new Error('Invalid GeoJSON object');
  }

  return JSON.stringify(convertFn(geoJson));
};

const typeMap = {
  POINT: 'esriGeometryPoint',
  MULTIPOINT: 'esriGeometryMultipoint',
  LINESTRING: 'esriGeometryPolyline',
  MULTILINESTRING: 'esriGeometryPolyline',
  POLYGON: 'esriGeometryPolygon',
  MULTIPOLYGON: 'esriGeometryPolygon',
};

type ConvertType = keyof typeof typeMap;

export const convertGeoJsonTypeToEsri = (geoJsonType: string) => {
  if (!geoJsonType || typeof geoJsonType !== 'string') {
    throw new Error('Invalid GeoJSON geometry type');
  }

  const convertType = geoJsonType.toUpperCase() as ConvertType;
  const esriType = typeMap[convertType];

  if (!esriType) {
    throw new Error(`Unsupported geometry type: ${geoJsonType}`);
  }

  return esriType;
};
